import {Suspense} from 'react';
import {useNavigate} from 'react-router';
import * as cciApi from '@di-shared/api/cci/auth';
import {diApi} from '@di-shared/api/di';
import {ApiResponseDemeritEducationApiResponseListDemeritEducationReserveResponse} from '@di-shared/api/di/__generated';
import {DEFAULT_CACHE_TIME} from '@di-shared/const';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {useSuspenseQueries} from '@tanstack/react-query';

export interface DemeritEducationReservationEditStep1CheckProps {
  vin: string;
  onNext(): void;
}

function DemeritEducationReservationEditStep1Check(props: DemeritEducationReservationEditStep1CheckProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  return (
    <Suspense
      fallback={
        <div className="wrap">
          <Header left={<HeaderBackButton />}>
            <h1 className="headline_xxsmall nav_center_title">{t('T10241')}</h1>
          </Header>
          <main className="content">
            <section className="section">
              <div className="section_container_header">
                <h2 className="title_small_b">{t('T10250')}</h2>
              </div>
              <div className="reservation_info_wrap">
                <div className="reservation_info_box">
                  <span className="title">{t('T10251')}</span>
                  <p className="reservation_info_text skeleton">홍길동</p>
                </div>
                <div className="reservation_info_box">
                  <span className="title">{t('T10252')}</span>
                  <p className="reservation_info_text skeleton">{t('T10253')}</p>
                </div>
                <div className="reservation_info_box">
                  <span className="title">{t('T10254')}</span>
                  <p className="reservation_info_text skeleton">{t('T10056_$score', {score: '-20'})}</p>
                </div>
              </div>
            </section>
            <section className="section">
              <div className="section_container_header">
                <h2 className="title_small_b">{t('T10280')}</h2>
              </div>
              <div className="reservation_info_wrap">
                <div className="reservation_info_box">
                  <span className="title">{t('T10261')}</span>
                  <p className="reservation_info_text skeleton">강남 도로교통교육장</p>
                </div>
                <div className="reservation_info_box">
                  <span className="title">{t('T10273')}</span>
                  <p className="reservation_info_text skeleton">서울시 강남구 구성로 313</p>
                </div>
                <div className="reservation_info_box">
                  <span className="title">{t('T10262')}</span>
                  <p className="reservation_info_text skeleton">{CldrDatetimeFormatter.format(new Date(), 'yMMMd')}</p>
                </div>
                <div className="reservation_info_box">
                  <span className="title">{t('T10263')}</span>
                  <p className="reservation_info_text skeleton">
                    {CldrDatetimeFormatter.format(new Date(), 'aHM')} ~ {CldrDatetimeFormatter.format(new Date(), 'aHM')}
                  </p>
                </div>
              </div>
            </section>
            <section className="section section_notice">
              <div className="section_container">
                <strong className="notice_title">{t('T10312')}</strong>
                <ul className="notice_list">
                  <li className="notice_item">{t('T10313')}</li>
                  <li className="notice_item">{t('T10315')}</li>
                </ul>
              </div>
              <div className="section_container">
                <strong className="notice_title">{t('T10259')}</strong>
                <ul className="notice_list">
                  <li className="notice_item">{t('T10257')}</li>
                  <li className="notice_item">{t('T10258')}</li>
                  <li className="notice_item">{t('T10260')}</li>
                </ul>
              </div>
              <div className="section_container">
                <strong className="notice_title">{t('T10274')}</strong>
                <ul className="notice_list">
                  <li className="notice_item">{t('T10277')}</li>
                  <li className="notice_item">{t('T10323')}</li>
                  <li className="notice_item">{t('T10324')}</li>
                  <li className="notice_item">{t('T10325')}</li>
                  <li className="notice_item">{t('T10326')}</li>
                  <li className="notice_item">{t('T10327')}</li>
                </ul>
              </div>
            </section>
          </main>
          <div className="floating_action_area">
            <button
              type="button"
              className="btn btn_large btn_outline btn_full skeleton"
            >
              {t('T10266')}
            </button>
            <button
              type="button"
              className="btn btn_large btn_outline btn_full skeleton"
            >
              {t('T10267')}
            </button>
          </div>
        </div>
      }
    >
      <Inner {...props} />
    </Suspense>
  );
}

function Inner({vin, onNext}: DemeritEducationReservationEditStep1CheckProps) {
  const {t} = useInferableTranslation();
  const {openConfirm, openAlert} = useCcWebviewUtilsHelper();
  const puid = environmentStore.getState().puid;
  const navigate = useNavigate();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const [{data: meData}, {data: reserveData}] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(cciApi.getMe)],
        queryFn: () => cciApi.getMe(),
        ...DEFAULT_CACHE_TIME,
      },
      {
        queryKey: [getQueryKey(diApi.demeritEducationController.getReserve)],
        queryFn: () => diApi.demeritEducationController.getReserve({}),
        select: ({data}: ApiResponseDemeritEducationApiResponseListDemeritEducationReserveResponse) => data.contents[0],
      },
    ],
  });

  // 예약 날짜 검증
  const eduDate = parseDateWithFormat(reserveData.eduDate);
  const startTime = parseDateWithFormat(reserveData.eduDate + reserveData.startTime);
  const endTime = parseDateWithFormat(reserveData.eduDate + reserveData.endTime);
  if (!eduDate || !startTime || !endTime) {
    throw new Error('demerit education date error');
  }

  const [deleteDemeritEducationReservation] = usePendingHandler(
    async () => {
      const isConfirm = await openConfirm({
        title: t('T10268'),
        message: t('T10269'),
        cancelText: t('T10221'),
        okText: t('T10247'),
      });
      if (isConfirm) {
        await diApi.demeritEducationController.deleteReserve({
          requestBody: {
            puid,
            eduDate: reserveData.eduDate,
            center: reserveData.code,
            classroomCode: reserveData.classroomCode,
            startTime: reserveData.startTime,
          },
        });
        navigate(ROUTE_PATHS.HOME_FINANCE_TAB.getFullPath(vin));
      }
    },
    async () => {
      await openAlert({
        title: t('T10314'),
        message: t('T10328'),
        okText: t('T10221'),
      });
    }
  );

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('T10241')}</h1>
      </Header>
      <main className="content">
        <section className="section">
          <div className="section_container_header">
            <h2 className="title_small_b">{t('T10250')}</h2>
          </div>
          <div className="reservation_info_wrap">
            <div className="reservation_info_box">
              <span className="title">{t('T10251')}</span>
              <p className="reservation_info_text">{meData.name}</p>
            </div>
            <div className="reservation_info_box">
              <span className="title">{t('T10252')}</span>
              <p className="reservation_info_text">{t('T10253')}</p>
            </div>
            <div className="reservation_info_box">
              <span className="title">{t('T10254')}</span>
              <p className="reservation_info_text">{t('T10056_$score', {score: '-20'})}</p>
            </div>
          </div>
        </section>
        <section className="section">
          <div className="section_container_header">
            <h2 className="title_small_b">{t('T10280')}</h2>
          </div>
          <div className="reservation_info_wrap">
            <div className="reservation_info_box">
              <span className="title">{t('T10261')}</span>
              <p className="reservation_info_text">{reserveData.name}</p>
            </div>
            <div className="reservation_info_box">
              <span className="title">{t('T10273')}</span>
              <p className="reservation_info_text">{reserveData.centerAddr}</p>
            </div>
            <div className="reservation_info_box">
              <span className="title">{t('T10262')}</span>
              <p className="reservation_info_text">{CldrDatetimeFormatter.format(eduDate, 'yMMMd')}</p>
            </div>
            <div className="reservation_info_box">
              <span className="title">{t('T10263')}</span>
              <p className="reservation_info_text">
                {CldrDatetimeFormatter.format(startTime, 'aHM')} ~ {CldrDatetimeFormatter.format(endTime, 'aHM')}
              </p>
            </div>
          </div>
        </section>
        <section className="section section_notice">
          <div className="section_container">
            <strong className="notice_title">{t('T10312')}</strong>
            <ul className="notice_list">
              <li className="notice_item">{t('T10313')}</li>
              <li className="notice_item">{t('T10315')}</li>
            </ul>
          </div>
          <div className="section_container">
            <strong className="notice_title">{t('T10259')}</strong>
            <ul className="notice_list">
              <li className="notice_item">{t('T10257')}</li>
              <li className="notice_item">{t('T10258')}</li>
              <li className="notice_item">{t('T10260')}</li>
            </ul>
          </div>
          <div className="section_container">
            <strong className="notice_title">{t('T10274')}</strong>
            <ul className="notice_list">
              <li className="notice_item">{t('T10277')}</li>
              <li className="notice_item">{t('T10323')}</li>
              <li className="notice_item">{t('T10324')}</li>
              <li className="notice_item">{t('T10325')}</li>
              <li className="notice_item">{t('T10326')}</li>
              <li className="notice_item">{t('T10327')}</li>
            </ul>
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={onNext}
        >
          {t('T10266')}
        </button>
        <button
          type="button"
          className="btn btn_large btn_outline btn_full"
          onClick={deleteDemeritEducationReservation}
        >
          {t('T10267')}
        </button>
      </div>
    </div>
  );
}

export default DemeritEducationReservationEditStep1Check;
